export const PERFECT_ORDER_STATUS = {
  Open: 33,
  Closed: 34,
};

export const SPECIAL_ORDER_KEYWORDS = ['POTATO', 'ONION', 'PALLET ALLEY'];

export const QTY_UNIT_OPTIONS = ['Each', 'Case', 'Pallet'] as const;
export type QtyUnit = (typeof QTY_UNIT_OPTIONS)[number];
export const DEFAULT_QTY_UNIT: QtyUnit = 'Each';

export interface PerfectOrderItem {
  perfectOrderLineId?: number;
  perfectOrderId?: number;
  retOrderLine?: number;
  vendorNumber?: number;
  itemNumber?: string;
  description?: string;
  upc?: string;
  quantity?: number;
  quantityUnit?: QtyUnit;
  customerPrice?: number | null;
  stockDesignationId?: number;
  primaryStockStatus?: string;
  backupStockStatus?: string;
  selected?: boolean;
  pendingDelete?: boolean;
  originalQuantity?: number;
  originalCustomerPrice?: number;
  kitParentItemNumber?: string;
}

export interface PerfectOrderModalItem extends PerfectOrderItem {
  lineNumber?: number;
  description?: string;
  selected?: boolean;
  maxQuantity?: number;
  upc?: string;
}

export interface KitComponent {
  kitItemNumber: string;
  kitType: string;
  itemNumber: string;
  description?: string;
  vendorNumber?: number;
  quantityNeeded: number;
  price: number;
}

export interface KitItem {
  kitItemNumber: string;
  kitType: string;
  components: KitComponent[];
}

export interface PerfectOrderNoteResponse {
  perfectOrderNoteId: number;
  perfectOrderId: number;
  note: string;
  createdBy?: number | null;
  createDate?: string | null;
}

export interface PerfectOrderFileResponse {
  perfectOrderFileId: number;
  perfectOrderId: number;
  fileUrl: string;
  fileName?: string;
  contentType?: string;
  fileSize?: number;
  createdBy?: number | null;
  createDate?: string | null;
}

export interface PerfectOrderInvoiceAddonsResponse {
  addonNo: number;
  addonAmount: number | string;
  addonNetAmount: number | string;
  description?: string;
  addonType?: string;
}

export interface PerfectOrder {
  perfectOrderId: number;
  orderNumber?: number;
  orderSuffix?: number;
  customerNumber?: number;
  shipTo?: string;
  warehouse?: string;
  backupWarehouse?: string;
  transactionType?: string;
  rootCauseId?: number;
  buyer?: string;
  claimTypeId?: number;
  customerContactName?: string;
  customerContactPhone?: string;
  customerContactEmail?: string;
  requesterEmail?: string;
  notifyOnSubmitEmails?: string;
  requesterComment?: string;
  assignedTo?: number;
  statusId?: number;
  isDnsNeeded?: boolean;
  dnsNumber?: string;
  isCreditNeeded?: boolean;
  creditNumber?: string;
  reasonForCreditId?: number;
  dateOpened?: string;
  dateClosed?: string;
  returnReasonId?: number;
  source?: string;
  shippedDate?: string;
  invoicedDate?: string;
  totalAmount?: number;
  perfectOrderItems?: PerfectOrderItem[];
  perfectOrderFiles?: PerfectOrderFileResponse[];
  perfectOrderNotes?: PerfectOrderNoteResponse[];
  invoiceAddons?: PerfectOrderInvoiceAddonsResponse[];
}

export type CreatePerfectOrderRequest = Pick<
  PerfectOrder,
  | 'orderNumber'
  | 'orderSuffix'
  | 'customerNumber'
  | 'shipTo'
  | 'warehouse'
  | 'backupWarehouse'
  | 'buyer'
  | 'claimTypeId'
  | 'customerContactName'
  | 'customerContactPhone'
  | 'customerContactEmail'
  | 'requesterEmail'
  | 'notifyOnSubmitEmails'
  | 'requesterComment'
  | 'assignedTo'
  | 'statusId'
  | 'reasonForCreditId'
  | 'dateOpened'
  | 'dateClosed'
  | 'returnReasonId'
  | 'source'
  | 'perfectOrderItems'
> & {
  customerName?: string;
  invoiceAddons?: PerfectOrderInvoiceAddonsResponse[];
};

export interface PerfectOrderModalFormValues {
  orderNumber: number;
  customerNumber: string;
  customerName: string;
  warehouseNumber: string;
  orderSuffix: number;
  shipToNumber: string;
  requesterComment: string;
  customerContactName: string;
  customerContactEmail: string;
  customerContactPhone: string;
  requesterEmail: string;
  notifyOnSubmitEmails: string;
  claimTypeId?: number;
  reasonForCreditId?: number;
  returnReasonId?: number;
  perfectOrderItems?: PerfectOrderModalItem[];
  invoiceAddons?: PerfectOrderInvoiceAddonsResponse[];
  selectedInvoiceAddonNos?: number[];
  perfectOrderFiles?: File[];
}

type PerfectOrderModalInitializableField = Exclude<keyof PerfectOrderModalFormValues, 'perfectOrderFiles'>;

export type PerfectOrderModalInitialValues = Partial<Pick<PerfectOrderModalFormValues, PerfectOrderModalInitializableField>>;

export interface PerfectOrderSubmissionContext {
  source: string;
  submittedAt?: string;
  customerNumber?: number;
  customerName?: string;
  customerContactName?: string;
  customerContactEmail?: string;
  userEmail?: string;
  orderNumber?: number;
  orderSuffix?: number;
  shipTo?: string;
  warehouse?: string;
  perfectOrderItems?: PerfectOrderItem[];
  statusId?: number;
}

export const mapSelectedPerfectOrderItems = (items?: PerfectOrderModalItem[]): PerfectOrderItem[] | undefined => {
  const selectedItems = items
    ?.filter((item) => item.selected)
    .map(({ retOrderLine, vendorNumber, itemNumber, description, quantity, quantityUnit, customerPrice, stockDesignationId }) => ({
      retOrderLine,
      vendorNumber,
      itemNumber,
      description,
      quantity,
      quantityUnit: quantityUnit ?? DEFAULT_QTY_UNIT,
      customerPrice,
      stockDesignationId,
    }));

  return selectedItems && selectedItems.length > 0 ? selectedItems : undefined;
};

export const buildPerfectOrderSubmissionPayload = (form: PerfectOrderModalFormValues, context: PerfectOrderSubmissionContext): CreatePerfectOrderRequest => ({
  customerNumber: context.customerNumber,
  customerName: form.customerName || context.customerName || undefined,
  shipTo: form.shipToNumber || context.shipTo || undefined,
  warehouse: form.warehouseNumber || context.warehouse || undefined,
  customerContactName: form.customerContactName || context.customerContactName,
  customerContactEmail: form.customerContactEmail || context.customerContactEmail,
  customerContactPhone: form.customerContactPhone || undefined,
  requesterEmail: form.requesterEmail || context.userEmail,
  notifyOnSubmitEmails: form.notifyOnSubmitEmails || undefined,
  requesterComment: form.requesterComment || undefined,
  source: context.source,
  dateOpened: context.submittedAt ?? new Date().toISOString(),
  orderNumber: form.orderNumber ?? context.orderNumber,
  orderSuffix: form.orderSuffix ?? context.orderSuffix,
  claimTypeId: form.claimTypeId,
  statusId: context.statusId ?? PERFECT_ORDER_STATUS.Open,
  dateClosed: null,
  reasonForCreditId: form.reasonForCreditId,
  returnReasonId: form.returnReasonId,
  perfectOrderItems: mapSelectedPerfectOrderItems(form.perfectOrderItems),
  invoiceAddons: form.invoiceAddons?.filter((addon) => (form.selectedInvoiceAddonNos ?? []).includes(addon.addonNo)),
});

export interface CreatePerfectOrderNoteRequest {
  Note: string;
}

export interface PerfectOrderView {
  perfectOrderId: number;
  orderNumber?: number;
  orderSuffix?: number;
  fullOrderNumber?: string;
  sortableOrderNumber?: number;
  customerNumber?: number;
  customerName?: string;
  shipTo?: string;
  warehouse?: string;
  backupWarehouse?: string;
  transactionType?: string;
  rootCause?: string;
  buyer?: string;
  claimType?: string;
  customerContactName?: string;
  customerContactPhone?: string;
  customerContactEmail?: string;
  requesterEmail?: string;
  assignedTo?: number;
  status?: string;
  isDnsNeeded?: boolean;
  dnsNumber?: string;
  isCreditNeeded?: boolean;
  creditNumber?: string;
  reasonForCredit?: string;
  dateOpened?: string;
  dateClosed?: string;
  shippedDate?: string;
  invoicedDate?: string;
  returnReason?: string;
  source?: string;
  hardGoodsRep?: string;
  lawnGardenRep?: string;
  plantRep?: string;
}

export interface PerfectOrderDropdownValueResult {
  dropdownValueId: number;
  dropdownType?: string;
  dropdownValue?: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface ItemClaimType {
  claimTypeId: number;
  claimType: string;
  isActive?: boolean;
}
